/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * TanStack Query hooks for Integrations
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  authenticatedApi,
  unwrapResponse,
  unwrapPaginatedResponse,
} from "@/api/authenticatedClient";
import type { ApiResponse, PaginatedData } from "@/lib/types";
import { integrationConfigSchema } from "@/lib/schemas/integrations";
import type { Integration } from "@/components/integrations/types";

// ============================================================================
// Types
// ============================================================================

type IntegrationConfig = z.infer<typeof integrationConfigSchema>;

interface IntegrationFilters {
  integration_type?: string;
  is_enabled?: boolean;
  name?: string;
  page?: number;
  page_size?: number;
}

interface CreateIntegrationInput {
  name: string;
  integration_type: string;
  config: IntegrationConfig;
  is_enabled?: boolean;
}

interface UpdateIntegrationInput {
  name?: string;
  config?: IntegrationConfig;
  is_enabled?: boolean;
}

// ============================================================================
// Query Keys
// ============================================================================

export const integrationKeys = {
  all: ["integrations"] as const,
  lists: () => [...integrationKeys.all, "list"] as const,
  list: (filters: IntegrationFilters) => [...integrationKeys.lists(), filters] as const,
  details: () => [...integrationKeys.all, "detail"] as const,
  detail: (id: string) => [...integrationKeys.details(), id] as const,
  sftpConnection: () => [...integrationKeys.all, "sftp-connection"] as const,
};

/** Matches FastAPI `SftpConnectionInfo` JSON (snake_case fields). */
export interface SftpConnectionInfo {
  configured: boolean;
  host?: string | null;
  port?: number | null;
  username?: string | null;
  /** Default remote base path from API (`remote_path`). */
  remote_path?: string | null;
  /** Fallback if a client camel-cases the payload; prefer `remote_path`. */
  remotePath?: string | null;
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch all integrations
 */
export function useIntegrations(filters: IntegrationFilters = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: integrationKeys.list(filters),
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<PaginatedData<Integration>>>(
        "/integrations/",
        {
          params: {
            integration_type: filters.integration_type,
            is_enabled: filters.is_enabled,
            name: filters.name,
            page: filters.page,
            page_size: filters.page_size,
          },
        }
      );
      return unwrapPaginatedResponse(response);
    },
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

const INCIDENT_INTEGRATION_TYPES = new Set(["jira", "servicenow", "azure_devops"]);

/**
 * Returns true when at least one enabled external integration (jira, servicenow, azure_devops) exists.
 * Used to gate the Incidents feature in nav / routes.
 */
export function useHasExternalIntegration() {
  const { data, isLoading } = useIntegrations({ is_enabled: true, page_size: 50 });
  const hasIntegration =
    data?.data.some((i) => INCIDENT_INTEGRATION_TYPES.has(i.integration_type)) ?? false;
  return { hasIntegration, isLoading };
}

/**
 * Fetch SFTP connection display info (for submission Step 2).
 * Safe display-only; no credentials. Any authenticated user can call.
 */
export function useSftpConnectionInfo() {
  return useQuery({
    queryKey: integrationKeys.sftpConnection(),
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<SftpConnectionInfo>>(
        "/integrations/sftp-connection"
      );
      return unwrapResponse(response);
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Fetch single integration by ID
 */
export function useIntegration(id: string | undefined) {
  return useQuery({
    queryKey: integrationKeys.detail(id ?? ""),
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<Integration>>(
        `/integrations/${id ?? ""}`
      );
      return unwrapResponse(response);
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Create new integration
 */
export function useCreateIntegration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateIntegrationInput) => {
      const response = await authenticatedApi.post<ApiResponse<Integration>>(
        "/integrations/",
        data
      );
      return unwrapResponse(response);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: integrationKeys.lists() });
    },
  });
}

/**
 * Update existing integration
 */
export function useUpdateIntegration(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateIntegrationInput) => {
      const response = await authenticatedApi.put<ApiResponse<Integration>>(
        `/integrations/${id}`,
        data
      );
      return unwrapResponse(response);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: integrationKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: integrationKeys.lists() });
    },
  });
}

/**
 * Delete integration
 */
export function useDeleteIntegration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await authenticatedApi.delete(`/integrations/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: integrationKeys.lists() });
    },
  });
}

/**
 * Test integration connection
 */
export function useTestIntegration(id: string) {
  return useMutation({
    mutationFn: async () => {
      const response = await authenticatedApi.post<
        ApiResponse<{ success: boolean; message: string }>
      >(`/integrations/${id}/test`);
      return unwrapResponse(response);
    },
  });
}

/**
 * Sync integration
 */
export function useSyncIntegration(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await authenticatedApi.post<ApiResponse<{ task_id: string }>>(
        `/integrations/${id}/sync`
      );
      return unwrapResponse(response);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: integrationKeys.detail(id) });
    },
  });
}
